import { beginCallActivity } from "./callActivity";
import { createOutgoingTone } from "./callSounds";
// src/components/meet/callSession.js
import { db } from "../../firebase/firebase.utils";
import { enterCall, exitCall, notifyCallMembers } from "./callRoom";
import {
  addParticipant,
  removeParticipant,
  updateParticipant,
  resetParticipants,
} from "../../redux/videocall/videoCallSlice";

// Same transport and wire format as mobile. Camera/mic and screen use separate
// peer connections; never replace a camera track with a screen track.
const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];
const description = ({ type, sdp }) => ({ type, sdp });
const stopTracks = (stream) =>
  stream?.getTracks().forEach((track) => {
    track.onended = null;
    track.stop();
  });

export const idleCall = {
  phase: "idle",
  localStream: null,
  screenStream: null,
  isMuted: false,
  isCameraOn: true,
  shareBusy: false,
  error: "",
  ringtoneBlocked: false,
  endedRemotely: false,
  target: null,
};

export function createCallSession({ target, dispatch, onChange }) {
  // Captured once: changing the selected chat cannot move an ongoing call.
  const { roomId, userId, userName } = target;
  const room = db.collection("rooms").doc(roomId);
  const participantRef = room.collection("participants").doc(userId);
  const peers = new Map();
  const members = new Map();
  const confirmedMembers = new Set();
  let callId = null;
  let releaseActivity;
  const tone = createOutgoingTone((ringtoneBlocked) => {
    if (active) publish({ ringtoneBlocked });
  });
  let ringTimer = null;
  let ringFinished = false;
  const subscriptions = [];
  const writes = new Set();
  let state = { ...idleCall, target };
  let active = true;
  let screenGeneration = 0;
  let stoppingScreen = null;
  let leaving = null;

  const publish = (patch) => {
    state = { ...state, ...patch };
    onChange(state);
  };
  const report = (error) => {
    console.error("Meeting:", error);
    if (error?.code === "permission-denied") leave({ remote: true });
    if (active)
      publish({
        error: error?.message || "A call connection could not be updated.",
      });
  };
  const write = async (promise, entry) => {
    writes.add(promise);
    entry?.writes.add(promise);
    try {
      return await promise;
    } finally {
      writes.delete(promise);
      entry?.writes.delete(promise);
    }
  };
  const listen = (ref, handler, list = subscriptions) => {
    list.push(
      ref.onSnapshot(
        { includeMetadataChanges: true },
        (snapshot) => {
          if (active) handler(snapshot);
        },
        report,
      ),
    );
  };
  const closePeer = (key) => {
    const entry = peers.get(key);
    if (!entry) return;
    peers.delete(key);
    entry.subscriptions.forEach((unsubscribe) => unsubscribe());
    entry.pc.ontrack = null;
    entry.pc.onicecandidate = null;
    entry.pc.close();
  };
  const deleteSignal = async (ref) => {
    await Promise.all(
      ["offerCandidates", "answerCandidates"].map(async (name) => {
        const snapshot = await ref.collection(name).get();
        await Promise.all(snapshot.docs.map((item) => item.ref.delete()));
      }),
    );
    await ref.delete();
  };
  const deleteSignals = async (kind, ownScreenOnly = false) => {
    const snapshot = await room.collection(kind).get();
    const owned = snapshot.docs.filter((item) => {
      const data = item.data();
      if (data.callSessionId !== callId) return false;
      return kind === "connections"
        ? data.requester === userId || data.responder === userId
        : data.sharerId === userId ||
            (!ownScreenOnly && data.viewerId === userId);
    });
    await Promise.all(owned.map((item) => deleteSignal(item.ref)));
  };

  async function connect(kind, peerId, incoming = null) {
    if (!active || peerId === userId) return;
    const screen = kind === "screenConnections";
    const outgoing = !incoming;
    if (!screen && (outgoing ? userId > peerId : peerId > userId)) return;
    const stream = screen
      ? outgoing
        ? state.screenStream
        : null
      : state.localStream;
    if (outgoing && !stream) return;
    const id = outgoing ? `${userId}-${peerId}` : `${peerId}-${userId}`;
    const key = `${kind}/${id}`;
    if (peers.has(key)) return;
    const ref = room.collection(kind).doc(`${callId}_${id}`);
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const entry = {
      pc,
      ref,
      kind,
      peerId,
      outgoing,
      subscriptions: [],
      writes: new Set(),
      offerSdp: incoming?.offer?.sdp,
      pendingCandidates: [],
      localCandidates: [],
      offerReady: !outgoing,
      iceChain: Promise.resolve(),
      answering: false,
    };
    peers.set(key, entry);
    const current = () => active && peers.get(key) === entry;
    const queueCandidate = (candidate) => {
      // Firestore can deliver trickled ICE before the SDP answer snapshot.
      if (!pc.remoteDescription) {
        entry.pendingCandidates.push(candidate);
        return;
      }
      entry.iceChain = entry.iceChain
        .then(async () => {
          if (current())
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
        })
        .catch((error) => {
          if (current()) report(error);
        });
    };
    const setRemote = async (sdp) => {
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      if (!current()) return;
      entry.pendingCandidates.splice(0).forEach(queueCandidate);
    };
    const localCandidates = outgoing ? "offerCandidates" : "answerCandidates";
    const remoteCandidates = outgoing ? "answerCandidates" : "offerCandidates";
    stream?.getTracks().forEach((track) => pc.addTrack(track, stream));
    const sendCandidate = ({ candidate }) => {
      if (!entry.offerReady) {
        if (candidate) entry.localCandidates.push(candidate);
        return;
      }
      if (candidate && current())
        write(
          ref.collection(localCandidates).add(candidate.toJSON()),
          entry,
        ).catch(report);
    };
    pc.onicecandidate = sendCandidate;
    pc.ontrack = (event) => {
      if (!current()) return;
      const remoteStream =
        event.streams[0] || entry.remoteStream || new MediaStream();
      if (!remoteStream.getTracks().includes(event.track))
        remoteStream.addTrack(event.track);
      entry.remoteStream = remoteStream;
      dispatch(
        updateParticipant({
          userId: peerId,
          ...(screen
            ? { screenStream: remoteStream, screenSharing: true }
            : { stream: remoteStream }),
        }),
      );
    };

    try {
      if (outgoing) {
        const offer = await pc.createOffer();
        if (!current()) return;
        await pc.setLocalDescription(offer);
        if (!current()) return;
        await write(
          ref.set({
            callSessionId: callId,
            offer: description(offer),
            ...(screen
              ? { sharerId: userId, viewerId: peerId }
              : { requester: userId, responder: peerId }),
          }),
          entry,
        );
        entry.offerReady = true;
        entry.localCandidates
          .splice(0)
          .forEach((candidate) => sendCandidate({ candidate }));
        if (!current()) return;
        listen(
          ref,
          (snapshot) => {
            const answer = snapshot.data()?.answer;
            if (
              !current() ||
              !answer ||
              pc.remoteDescription ||
              entry.answering
            )
              return;
            entry.answering = true;
            setRemote(answer).catch((error) => {
              if (current()) report(error);
            });
          },
          entry.subscriptions,
        );
      } else {
        await setRemote(incoming.offer);
        if (!current()) return;
        const answer = await pc.createAnswer();
        if (!current()) return;
        await pc.setLocalDescription(answer);
        if (!current()) return;
        await write(ref.update({ answer: description(answer) }), entry);
      }
      if (!current()) return;
      listen(
        ref.collection(remoteCandidates),
        (snapshot) => {
          if (!current()) return;
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") queueCandidate(change.doc.data());
          });
        },
        entry.subscriptions,
      );
    } catch (error) {
      if (current()) {
        closePeer(key);
        report(error);
      }
    }
  }

  function listenForCalls(kind) {
    const screen = kind === "screenConnections";
    listen(room.collection(kind), (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        if (
          data.callSessionId !== callId ||
          (screen ? data.viewerId : data.responder) !== userId
        )
          return;
        const peerId = screen ? data.sharerId : data.requester;
        const key = `${kind}/${peerId}-${userId}`;
        if (change.type === "removed") {
          closePeer(key);
          if (screen)
            dispatch(
              updateParticipant({
                userId: peerId,
                screenSharing: false,
                screenStream: null,
              }),
            );
          return;
        }
        if (!data.offer) return;
        // A new offer can overwrite an old document after another client rejoins.
        const previous = peers.get(key);
        if (previous && previous.offerSdp !== data.offer.sdp) closePeer(key);
        connect(kind, peerId, data);
      });
    });
  }

  function listenForMembers() {
    listen(room.collection("participants"), (snapshot) => {
      if (!snapshot.metadata.fromCache)
        snapshot.docs.forEach((item) => {
          const person = item.data();
          if (
            person.userId !== userId &&
            (!person.callSessionId || person.callSessionId === callId)
          )
            confirmedMembers.add(person.userId);
        });
      snapshot.docChanges().forEach((change) => {
        if (!active) return;
        const person = change.doc.data();
        const peerId = person.userId;
        if (
          !peerId ||
          peerId === userId ||
          (person.callSessionId && person.callSessionId !== callId)
        )
          return;
        if (change.type === "removed") {
          members.delete(peerId);
          for (const [key, entry] of peers)
            if (entry.peerId === peerId) closePeer(key);
          dispatch(removeParticipant(peerId));
          if (
            target.callType === "private" &&
            !snapshot.metadata.fromCache &&
            confirmedMembers.has(peerId)
          )
            leave({ remote: true });
          confirmedMembers.delete(peerId);
          return;
        }
        members.set(peerId, person);
        dispatch(
          addParticipant({
            userId: peerId,
            name: person.name,
            mic: person.mic,
            camera: person.camera,
            screenSharing: !!person.screenSharing,
            ...(!person.screenSharing ? { screenStream: null } : {}),
          }),
        );
        if (change.type === "added") {
          connect("connections", peerId);
          if (state.screenStream) connect("screenConnections", peerId);
        }
      });
    });
  }

  async function notifyOthers() {
    const people = Array.isArray(target.users)
      ? target.users
      : Object.values(target.users || {});
    await write(
      notifyCallMembers({
        room,
        callId,
        userId,
        recipientIds: people.map((person) =>
          typeof person === "string"
            ? person
            : person?.uid || person?.userId || person?.id,
        ),
        isActive: () => active,
        details: {
          fromImg:
            target.currentUser?.imageUrl ||
            target.currentUser?.photoURL ||
            null,
          fromName: userName || "Someone",
          click_action: roomId,
          type: `call${target.callType || "private"}`,
          callType: target.callType || "private",
          spaceId: target.spaceId || null,
          stationId: target.stationId || null,
        },
      }),
    );
  }

  async function join(startWithCamera = true) {
    if (state.phase !== "idle" || !active) return;
    try {
      releaseActivity = beginCallActivity(roomId, userId);
    } catch (error) {
      publish({ error: error.message });
      return;
    }
    publish({ phase: "joining", error: "" });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      if (!active) {
        stopTracks(stream);
        return;
      }
      stream.getVideoTracks().forEach((track) => {
        track.enabled = startWithCamera;
      });
      publish({ localStream: stream, isCameraOn: startWithCamera });
      const person = {
        userId,
        name: userName || "Someone",
        mic: true,
        camera: startWithCamera,
        screenSharing: false,
      };
      await write(
        enterCall(room, person, target.callType || "private", {
          spaceId: target.spaceId,
          stationId: target.stationId,
          otherUid: target.currentrecipient?.uid || target.currentrecipient?.id,
          expectedCallId: target.expectedCallId,
        }).then((id) => {
          callId = id;
        }),
      );
      if (!active) return;
      dispatch(addParticipant(person));
      listenForMembers();
      listen(room, (snapshot) => {
        // Cached room data may belong to a previous call.
        if (snapshot.metadata.fromCache) return;
        const data = snapshot.data();
        if (data?.callSessionId !== callId || data.callEndedAt) {
          leave({ remote: true });
          return;
        }
        const waiting =
          data.callStarterId === userId &&
          !(data.callParticipantIds || []).some((id) => id !== userId);
        if (!waiting) {
          ringFinished = true;
          clearTimeout(ringTimer);
          tone.stop();
        } else if (!ringFinished && !ringTimer) {
          tone.start();
          ringTimer = setTimeout(
            () => {
              ringFinished = true;
              tone.stop();
              if (active && target.callType === "private")
                leave({ remote: true });
            },
            Math.max(
              0,
              60000 -
                (Date.now() - (data.callStartedAt?.toMillis?.() || Date.now())),
            ),
          );
        }
      });
      listenForCalls("connections");
      listenForCalls("screenConnections");
      publish({ phase: "joined" });
      notifyOthers().catch((error) => {
        console.error("Call invitations:", error);
        if (active)
          publish({
            error:
              "The call is open, but invitations could not be sent. Leave and try again.",
          });
      });
    } catch (error) {
      if (!active) return;
      const message =
        error.message || "Check camera/microphone permissions and try again.";
      leave();
      publish({ error: message });
    }
  }

  function stopScreenShare() {
    if (stoppingScreen) return stoppingScreen;
    screenGeneration += 1;
    stopTracks(state.screenStream);
    const pending = [];
    for (const [key, entry] of peers) {
      if (entry.kind === "screenConnections" && entry.outgoing) {
        pending.push(...entry.writes);
        closePeer(key);
      }
    }
    publish({ screenStream: null, shareBusy: true });
    if (active)
      dispatch(
        updateParticipant({ userId, screenSharing: false, screenStream: null }),
      );
    stoppingScreen = Promise.allSettled([
      write(participantRef.update({ screenSharing: false })),
      Promise.allSettled(pending).then(() =>
        deleteSignals("screenConnections", true),
      ),
    ])
      .then((results) => {
        if (results[1].status === "rejected") report(results[1].reason);
      })
      .finally(() => {
        stoppingScreen = null;
        if (active) publish({ shareBusy: false });
      });
    return stoppingScreen;
  }

  async function startScreenShare() {
    if (
      !active ||
      state.phase !== "joined" ||
      state.shareBusy ||
      state.screenStream
    )
      return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      publish({
        error:
          "This browser cannot share a screen. Use a supported desktop browser or the Antlogy mobile app.",
      });
      return;
    }
    const generation = ++screenGeneration;
    publish({ shareBusy: true, error: "" });
    try {
      // Called directly from a click, before any await: preserve user activation.
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      if (!active || generation !== screenGeneration) {
        stopTracks(stream);
        return;
      }
      const track = stream.getVideoTracks()[0];
      if (!track || track.readyState === "ended") {
        stopTracks(stream);
        throw new Error("No screen video is available.");
      }
      track.onended = stopScreenShare;
      publish({ screenStream: stream });
      await write(participantRef.update({ screenSharing: true }));
      if (!active || generation !== screenGeneration) return;
      dispatch(updateParticipant({ userId, screenSharing: true }));
      await Promise.all(
        [...members.keys()].map((peerId) =>
          connect("screenConnections", peerId),
        ),
      );
    } catch (error) {
      if (!active || generation !== screenGeneration) return;
      if (state.screenStream) stopScreenShare();
      if (error.name !== "NotAllowedError" && error.name !== "AbortError")
        report(error);
    } finally {
      if (active && generation === screenGeneration && !stoppingScreen)
        publish({ shareBusy: false });
    }
  }

  function leave({ remote = false } = {}) {
    if (leaving) return leaving;
    active = false;
    clearTimeout(ringTimer);
    tone.dispose();
    screenGeneration += 1;
    subscriptions.splice(0).forEach((unsubscribe) => unsubscribe());
    for (const key of peers.keys()) closePeer(key);
    stopTracks(state.localStream);
    stopTracks(state.screenStream);
    dispatch(resetParticipants());
    publish({
      phase: "leaving",
      endedRemotely: remote,
      localStream: null,
      screenStream: null,
      shareBusy: false,
    });
    // No UI wait. Existing writes finish before reused signaling IDs are deleted.
    leaving = Promise.allSettled([
      Promise.allSettled([...writes]),
      stoppingScreen,
    ])
      .then(() =>
        Promise.allSettled([
          exitCall(room, userId, callId),
          deleteSignals("connections"),
          deleteSignals("screenConnections"),
        ]),
      )
      .then((results) =>
        results.forEach((result) => {
          if (result.status === "rejected")
            console.error("Call cleanup:", result.reason);
        }),
      )
      .finally(() => {
        releaseActivity?.();
        publish({ ...idleCall, error: state.error });
      });
    return leaving;
  }

  function toggleTrack(kind) {
    if (!active || state.phase !== "joined") return;
    const track =
      kind === "audio"
        ? state.localStream.getAudioTracks()[0]
        : state.localStream.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    const patch =
      kind === "audio" ? { mic: track.enabled } : { camera: track.enabled };
    publish(
      kind === "audio"
        ? { isMuted: !track.enabled }
        : { isCameraOn: track.enabled },
    );
    dispatch(updateParticipant({ userId, ...patch }));
    write(participantRef.update(patch)).catch(report);
  }

  return {
    get state() {
      return state;
    },
    join,
    resumeRingtone: () => tone.resume(),
    leave,
    startScreenShare,
    stopScreenShare,
    toggleMute: () => toggleTrack("audio"),
    toggleCamera: () => toggleTrack("video"),
  };
}
